import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
} from "@angular/core";
import {
  ControlContainer,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  FormGroupDirective,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatMenuTrigger } from "@angular/material/menu";
import { ConfirmationDialogComponent } from "@dialogs/confirmation/confirmation.component";
import { SearchDialogComponent } from "@dialogs/advanced-search/search.component";
import { RenameDialogComponent } from "@dialogs/rename/rename.component";
import {
  faSearch,
  faExclamationTriangle,
} from "@fortawesome/pro-light-svg-icons";
import {
  faPlus,
  faPencil,
  faMagnifyingGlassPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { faEdit, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { CommonService } from "@services/common.service";
import { DatapointService } from "@services/datapoint.service";
import { CanvasService } from "@services/canvas.service";
import { timer, Subscription } from "rxjs";
import {
  debounceTime,
  filter,
  map,
  pairwise,
  throttleTime,
} from "rxjs/operators";

@Component({
    selector: "app-data-field",
    templateUrl: "./data-field.component.html",
    styleUrls: ["./data-field.component.scss"],
    viewProviders: [
        { provide: ControlContainer, useExisting: FormGroupDirective },
    ],
    standalone: false
})
export class DataFieldComponent implements OnInit, OnChanges {
  @ViewChild("fieldMenuTrigger") fieldMenuTrigger!: MatMenuTrigger;
  @ViewChild("scroller") scroller!: CdkVirtualScrollViewport;

  @Input() charts!: any;
  @Input() canvas_id!: any;
  @Input() chartData!: any;
  @Input() currentDataFields!: any;
  @Input() sortFields!: any;
  @Input() filterFields!: any;
  @Input() currentQuantifierFields!: any;
  @Input() canvasOptions!: any;
  @Input() expanded!: boolean;
  @Input() canAddModifiers!: boolean;
  @Input() reportloading!: boolean;
  @Input() chartType!: any;
  @Input() isVisible?: boolean;
  @Output() openSettingsChange = new EventEmitter<any>();
  @Output() chartDataChange = new EventEmitter<any>();
  @Output() sortFieldsChange = new EventEmitter<any>();
  @Output() filterFieldsChange = new EventEmitter<any>();
  public dataFields: any[] = [];
  public canvasFields: any[] = [];
  private readonly subscription: Subscription;

  constructor(
    private ngZone: NgZone,
    private fb: UntypedFormBuilder,
    public commonService: CommonService,
    public datapointService: DatapointService,
    private canvasService: CanvasService,
    public parentF: FormGroupDirective,
    public dialog: MatDialog,
    private readonly el: ElementRef,
  ) {
    this.subscription = this.commonService.removeField$.subscribe((reportFieldId: number) => {
      this.removeElementById(reportFieldId);
    });
  }
  public fieldForm!: UntypedFormGroup;
  public fieldLabel: string = "";
  public categories: any = [];
  public aggregateData: any = [];
  public valueData: any = [];

  public error: string = "";
  public page: number = 1;
  public count: number = 200;
  public moreFilters: boolean = true;
  public loading!: boolean;
  public fieldItem: any;
  childForm: any;
  selectedIndex = -1;
  faPlus = faPlus;
  faSearch = faSearch;
  faEdit = faEdit;
  faTrash = faTrash;
  faExclamationTriangle = faExclamationTriangle;
  faPencil = faPencil;
  faMagnifyingGlassPlus = faMagnifyingGlassPlus;

  search() {
    let searchTerm = this.fieldForm.controls["field"].value;
    if (searchTerm.length > 0) {
      if (searchTerm.length < 2) {
        this.error = "you have to provide at least 2 characters to search";
        setTimeout(() => (this.error = ""), 3000);
        return;
      }

      this.page = 1;
      this.moreFilters = true;
      this.datapointService
        .searchDataFields(searchTerm, this.count, this.page)
        .subscribe((success: any) => {
          if (success.data <= 0) {
            this.error = "No results found";
            this.dataFields = [];
            this.fieldMenuTrigger.closeMenu();
            setTimeout(() => (this.error = ""), 2000);
            return;
          } else {
            this.dataFields = success.data;

            this.fieldMenuTrigger.openMenu();

            if (success.data.length < this.count) {
              this.moreFilters = false;
            }
            if (this.moreFilters) {
              this.scroller
                .elementScrolled()
                .pipe(
                  map(() => this.scroller.measureScrollOffset("bottom")),
                  pairwise(),
                  filter(([y1, y2]) => y2 < y1 && y2 < 140),
                  throttleTime(500)
                )
                .subscribe(() => {
                  this.ngZone.run(() => {
                    this.fetchMore();
                  });
                });
            }
          }
        });
    }
  }

  fetchMore() {
    let searchTerm = this.fieldForm.controls["field"].value;
    this.page = this.page + 1;
    if (this.moreFilters) {
      this.datapointService
        .searchDataFields(searchTerm, this.count, this.page)
        .subscribe((success: any) => {
          this.loading = true;
          timer(1000).subscribe(() => {
            const searchData = success.data;
            this.dataFields = [...this.dataFields, ...searchData];
          });

          if (success.data.length < this.count) {
            this.moreFilters = false;
          }
        });
    } else {
      this.loading = false;
    }
  }

  openSearch() {
    const elem = "advancedSearch";
    const element = document.getElementById(elem);
    const rect = element
      ? element.getBoundingClientRect()
      : { top: 0, left: 0 };
    const dataSetListing = this.childForm.get("dataSetListing").value;
    const dialogRef = this.dialog.open(SearchDialogComponent, {
      disableClose: true,
      data: {
        message: "Remove this data point?",
        actionButton: "apply",
        dataSetListing: dataSetListing,
        existingDataFields: this.canvasFields,
      },
      panelClass: ["canvas-dialog", "dialog-left"],
      position: { top: rect.top - 60 + "px", left: rect.left + 50 + "px" },
      width: "75vw",
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        //this.childForm.get("drilldownEnabled").setValue(false);
        this.childForm.get("dataSetListing").setValue(result.datasets);
        var resultArray = [...result.selection];
        resultArray.forEach((item: any) => {
          this.dataFields.push(item);
        });

        let forEachPromise = new Promise<void>((resolve) => {
          resultArray.forEach((item: any, index, array) => {
            this.moveItemtoList(item.id, false);
            if (index === array.length - 1) resolve();
          });
        });

        forEachPromise.then(() => {
          this.buildAggregateData();
        });
      }
    });
  }

  selectLabel(event: any) {
    let textVal = "";
    if (event.target.innerText == "" || event.target.innerText == null) {
      textVal = event.path[2].innerText.toString().trim();
    } else {
      textVal = event.target.innerText.toString().trim();
    }
    this.fieldLabel = textVal;
  }

  itemInReport(id: number) {
    if (this.canvasFields.some((e) => e.id === id)) {
      /* this.canvasFields contains the element we're looking for */
      return "none";
    } else {
      return "block";
    }
  }

  moveItemtoList(id: number, refresh: boolean) {
    if (this.canvasFields.some((e) => e.id === id)) {
      /* this.canvasFields contains the element we're looking for */
      this.error = "Field already in list";
      setTimeout(() => (this.error = ""), 2000);
    } else {
      this.error = "";
      //this.childForm.get("drilldownEnabled").setValue(false);

      const itemtoMove = this.dataFields.filter((item: any) => {
        return item.id == id;
      });

      this.canvasFields.push(itemtoMove[0]);
      this.childForm.controls["collection"].setValue(
        itemtoMove[0].dataSet.data.id
      );

      let chartType = this.childForm.controls["chartType"].value;
      if (typeof chartType == "object") {
        chartType = chartType.details;
      }
      if (chartType == "Table" || chartType == "List") {
        this.aggregateData =
          this.childForm.get("visibleDataPointIds")!.value || [];
        this.aggregateData.push({ dataPointId: id, breakdown: null });

        this.childForm.get("visibleDataPointIds")!.setValue(this.aggregateData);
      } else {
        if (this.canvasFields.length < 2) {
          this.aggregateData =
            this.childForm.get("visibleDataPointIds")!.value || [];
          this.aggregateData.push({ dataPointId: id, breakdown: null });
          this.childForm
            .get("visibleDataPointIds")!
            .setValue(this.aggregateData);

          this.aggregateData = this.childForm.get("appliedSeries")!.value || [];

          this.aggregateData.push({
            dataPointId: id,
            breakDownDataPointId: null,
          });
          this.childForm.get("appliedSeries")!.setValue(this.aggregateData);
        }
      }
      //console.log(this.childForm.value);
      if (refresh) this.buildAggregateData();
      this.fieldForm.get("field")!.setValue("");
      const control = this.el.nativeElement.querySelector(".search-input");
      if (control) {
        control.focus();
      }
    }
  }

  removeElementById(reportFieldId: number) {
    // Locate the DOM element
    const element = document.getElementById(`filter-${reportFieldId}`);
    if (element) {
      element.remove(); // Remove the DOM element
    }

    // Call your existing method to update data
    const index = this.canvasFields.findIndex((field) => field.id === reportFieldId);
    if (index > -1) {
      this.removeDataField(null, index, reportFieldId);
    }
  }

  removeDataField(event: any, index: number, datapoint: number) {
    this.selectedIndex = index;
    const elem = "filter-" + index;

    const element = document.getElementById(elem);
    const rect = element
      ? element.getBoundingClientRect()
      : { top: 0, left: 0 };

    if (event) {
      event.stopPropagation();
      let message = "Remove this data field?";
      let details = [];
      let messageDetail = "";
      let width = "300px";
      const visibleDataPointIds = this.childForm.get(
        "visibleDataPointIds"
      ).value;
      const groupdata = this.childForm.get("groupdata").value;
      const sortdata = this.childForm.get("sortdata").value;
      const filterdata = this.childForm.get("filterdata").value;
      const appliedSeries = this.childForm.get("appliedSeries").value;
      console.log(appliedSeries);
      let found = false;
      groupdata.forEach((element: any) => {
        if (element.dataPointId == datapoint) {
          found = true;
        }
      });
      if (found) {
        details.push("Group By Field");
      }

      found = false;
      sortdata.forEach((element: any) => {
        if (element.dataPointId == datapoint) {
          found = true;
        }
      });
      if (found) {
        details.push("Sorts");
      }
      found = false;
      filterdata.forEach((element: any) => {
        if (element.dataPointId == datapoint) {
          found = true;
        }
      });
      if (found) {
        details.push("Filters");
      }
      found = false;
      appliedSeries.forEach((element: any) => {
        if (element.dataPointId == datapoint) {
          found = true;
        }
      });
      if (found) {
        details.push("Series");
      }

      // if (this.childForm.get("drilldownEnabled").value) {
      //   message = "Remove this data point?";
      //   details.push("Page Links");
      // }
      if (details.length > 0) {
        messageDetail =
          "Removing this data field will impact the following configuration sections:";
        width = "500px";
      }

      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        disableClose: true,
        data: {
          message: message,
          messageDetail: messageDetail,
          details: details,
          actionButton: "remove",
        },

        panelClass: ["canvas-dialog", "dialog-left"],
        position: { top: rect.top - 50 + "px", left: rect.left + 230 + "px" },
        width: width,
      });

      dialogRef.afterClosed().subscribe((result) => {
        this.selectedIndex = -1;

        if (result) {
          this.fieldForm.get("field")!.disable();

          let visibleData = this.childForm.get("visibleDataPointIds")!.value;
          const itemToRemove = this.canvasFields[index].id;
          if (visibleData) {
            for (var i = 0; i < visibleData.length; i++) {
              if (visibleData[i] === itemToRemove) {
                visibleData.splice(i, 1);
              }
            }
          }
          this.childForm.get("visibleDataPointIds")!.setValue(visibleData);

          let series = this.childForm.get("appliedSeries")!.value;
          const seriesToRemove = this.canvasFields[index].id;
          if (series) {
            for (var i = 0; i < series.length; i++) {
              if (series[i].dataPointId === seriesToRemove) {
                series.splice(i, 1);
              }
            }
          }
          this.childForm.get("appliedSeries")!.setValue(series);

          // this.childForm.get("drilldownEnabled").setValue(false);
          this.canvasFields.splice(index, 1);
          if (this.canvasFields.length <= 0) {
            this.childForm.controls["collection"].setValue(null);
          }
          this.buildAggregateData();
          this.fieldForm.get("field")!.enable();
        }
      });
    } else {
      this.canvasFields.splice(index, 1);
      if (this.canvasFields.length <= 0) {
        this.childForm.controls["collection"].setValue(null);
      }
      this.buildAggregateData();
      this.fieldForm.get("field")!.enable();
    }
  }

  renameDataField(event: any, field: any, index: number) {
    const elem = "filter-" + index;
    const element = document.getElementById(elem);
    const rect = element
      ? element.getBoundingClientRect()
      : { top: 0, left: 0 };

    this.selectedIndex = index;
    if (event) {
      event.stopPropagation();

      const dialogRef = this.dialog.open(RenameDialogComponent, {
        disableClose: true,
        data: {
          field: field,
        },
        width: "500px",
        panelClass: ["canvas-dialog", "dialog-left", "dialog-larger"],
        position: { top: rect.top - 100 + "px", left: rect.left + 230 + "px" },
      });

      dialogRef.afterClosed().subscribe((result) => {
        this.selectedIndex = -1;
        if (result) {
          this.canvasFields[index] = result;
          this.buildAggregateData();
        }
      });
    } else {
    }
  }

  selectDatapoint(condition: boolean, filterID: any) {
    if (condition) {
      this.selectedIndex = filterID;
    } else {
      this.selectedIndex = -1;
    }
  }

  buildAggregateData() {
    let temp_visibledatapoints = this.childForm.get(
      "visibleDataPointIds"
    )!.value;

    this.aggregateData = [];
    let aliasData: any = [];
    this.canvasFields.forEach((report) => {
      this.aggregateData.push(report.id);
      aliasData.push({ id: report.id, value: report.alias || report.name });
    });
    if (this.childForm) {
      this.childForm.get("dataPointIds")!.setValue(this.aggregateData);
    }

    let callPreview = true;

    this.aggregateData = [];

    // QUANTIFIERS ADDED
    const defaultValue =
      this.childForm.get("calculationMethod")!.value ||
      this.canvasOptions.quantifierOptions.data[0].default.data.value ||
      36;

    if (this.currentDataFields[0]) {
      const quantifiers = this.commonService.generateQuantifiers(
        this.currentDataFields,
        this.childForm.get("calculationId")!.value,
        defaultValue
      );

      this.childForm.get("quantifierdata")!.setValue(quantifiers);
    }

    // FILTER CHART SORT FIELDS
    const filteredSortFields = this.commonService.filterSorts(
      this.sortFields,
      this.childForm.get("dataPointIds")!.value
    );
    const sortAggregateData = this.commonService.generateSorts(
      null,
      null,
      filteredSortFields
    );

    this.sortFieldsChange.emit(filteredSortFields);
    this.childForm.get("sortdata")!.setValue(sortAggregateData);

    // FILTER CHART FILTER FIELDS
    const filteredFilterFields = this.commonService.filterFilters(
      this.filterFields,
      this.childForm.get("dataPointIds")!.value
    );
    const filterAggregateData =
      this.commonService.generateFilters(filteredFilterFields);

    this.filterFieldsChange.emit(filteredFilterFields);
    this.childForm.get("filterdata")!.setValue(filterAggregateData);

    if (this.currentDataFields.length == 1) {
      const groupAggregate: any = [];
      let data;
      if (
        this.canvasOptions &&
        this.canvasOptions.groupOptions &&
        this.canvasOptions.groupOptions.data
      ) {
        data = {
          dataPointId: this.currentDataFields[0].id,
          modifiers: [
            { id: this.canvasOptions.groupOptions.data[0].id, value: "" },
          ],
        };
      }

      const groupType = this.currentDataFields[0].type;

      if (groupType == "date") {
        this.childForm.get("isDateGroupRange")!.setValue(true);

        const groupDateRange = this.childForm.get("groupDateRange")!.value;
        if (groupDateRange == "")
          this.childForm.get("groupDateRange")!.setValue("M");
      } else {
        this.childForm.get("isDateGroupRange")!.setValue(false);
        this.childForm.get("groupDateRange")!.setValue("");
      }

      groupAggregate.push(data);
      this.childForm.get("groupdata").setValue(groupAggregate);
    }

    this.childForm.get("visibleDataPointIds")!.setValue(temp_visibledatapoints);
    // make refresh call and emit the chart data to the parent
    if (callPreview) {
      this.childForm.get("dataPointAliases")!.setValue(aliasData);

      this.canvasService.refreshCanvas(this.childForm).subscribe(
        (success: any) => {
          this.canvasService.triggerEnable();
          const data = success.data;

          this.canvasService.pushChartToStorage(data);

          this.chartDataChange.emit(data);
          const dataPointListSize =
            this.childForm.get("dataPointIds").value.length || 0;
          if (dataPointListSize === 1) {
            this.openSettings();
          }
        },
        (error: any) => {
          this.canvasService.triggerEnable();

          const data = this.canvasService.getChartFromStorage();

          this.chartDataChange.emit(data);

          const dataPointListSize =
            this.childForm.get("dataPointIds").value.length || 0;
          if (dataPointListSize === 1) {
            this.openSettings();
          }
        }
      );
    }
  }

  openSettings() {
    this.openSettingsChange.emit();
  }

  drop(event: CdkDragDrop<string[]>) {
    if (event.previousIndex != event.currentIndex) {
      // this.childForm.get("drilldownEnabled").setValue(false);
      moveItemInArray(
        this.canvasFields,
        event.previousIndex,
        event.currentIndex
      );
      this.buildAggregateData();
    }
  }

  getAppName(categories: any) {
    if (categories.length > 1) {
      return "GRC Suite";
    } else {
      return categories[0].name;
    }
  }

  initForm() {
    this.fieldForm = this.fb.group({
      label: new UntypedFormControl(),
      field: new UntypedFormControl(""),
      collection: new UntypedFormControl(),
    });
    // setting a debounce after keyup
    this.fieldItem = this.fieldForm.controls["field"].valueChanges
      .pipe(debounceTime(1000))
      .subscribe(() => this.search());
  }
  
  ngOnChanges() {
    if (this.currentDataFields) {
      this.canvasFields = this.currentDataFields;
      if (this.currentDataFields[0] && this.currentDataFields[0].dataSet) {
        if (this.childForm) {
          this.childForm.controls["collection"]?.setValue(
            this.currentDataFields[0].dataSet.data.id
          );
        }
      } else {
        if (this.childForm) {
          this.childForm.controls["collection"]?.setValue(null);
        }
      }
    }
    if (this.childForm) {
      this.chartType = this.childForm.controls["chartType"].value;
      if (this.chartType && typeof this.chartType == "object") {
        this.chartType = this.chartType.details;
      }
    }
  }

  ngOnInit(): void {
    this.childForm = this.parentF.form;
    if (this.childForm) {
      this.chartType = this.childForm.controls["chartType"].value;
    }
    this.initForm();
  }
}
